import { useLocalStorage } from '@pesto/hooks';
import { cn, SIDEBAR_MINIMIZED_LS_KEY } from '@pesto/utils';
import { ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { NavigationProps } from './Navigation.types';

import { Badge } from '../Badge';
import { ExpandableNav } from './ExpandableNav';

export const DesktopNavigation = (props: NavigationProps) => {
  const { links, homeLink, className, triggerClassName } = props;
  const [isMinimized, setIsMinimized] = useLocalStorage(SIDEBAR_MINIMIZED_LS_KEY, false);

  const handleToggle = () => {
    setIsMinimized((prev) => !prev);
  };

  return (
    <aside
      className={cn(
        `border-sidebar-border/25 bg-sidebar relative hidden w-full flex-none flex-col border-r transition-[width] duration-500 md:flex`,
        className,
        !isMinimized ? 'w-72' : 'w-[72px]',
      )}
    >
      <div className={'sticky top-0 flex flex-col'}>
        <div className="hidden px-5 pb-3 pt-10 lg:block">
          <Link
            to={homeLink.href}
            aria-label={homeLink.name}
            className="text-sidebar-text-selected-foreground flex h-full items-center gap-3 font-semibold"
          >
            {homeLink.Logo ? (
              <homeLink.Logo
                minimized={isMinimized}
                className={cn('shrink-0', isMinimized ? 'h-6 w-6' : 'h-7 w-auto', homeLink.logoClassName)}
              />
            ) : (
              <span className="flex items-center gap-2">
                <homeLink.icon className="h-6 w-6" />
                {!isMinimized && <span>{homeLink.name}</span>}
              </span>
            )}
            {!isMinimized && (homeLink.subtitle || homeLink.envBadge) && (
              <span className="flex h-full flex-col items-start justify-end gap-1.5">
                {homeLink.envBadge && (
                  <Badge
                    variant={homeLink.envBadge.variant ?? 'secondary'}
                    className={cn('py-0 text-[10px] uppercase tracking-wider text-white', homeLink.envBadge.className)}
                  >
                    {homeLink.envBadge.label}
                  </Badge>
                )}
                {homeLink.subtitle && (
                  <span className="text-sidebar-text/80 whitespace-pre-line text-[10px] font-semibold uppercase leading-tight tracking-[0.14em]">
                    {homeLink.subtitle}
                  </span>
                )}
              </span>
            )}
          </Link>
        </div>
        <ChevronLeft
          className={cn(
            'border-sidebar-border/30 bg-sidebar text-sidebar-text hover:text-sidebar-text-hover absolute -right-3 top-10 z-50 cursor-pointer rounded-lg border text-3xl transition-colors',
            isMinimized && 'rotate-180',
          )}
          onClick={handleToggle}
        />
        <div className="flex h-full flex-col justify-between gap-2 px-2 pb-4 pt-1">
          <div className="flex h-full w-full flex-1">
            <nav className="grid w-full items-start text-sm font-medium">
              <ExpandableNav links={links} minimized={isMinimized} triggerClassName={triggerClassName} />
            </nav>
          </div>
        </div>
      </div>
    </aside>
  );
};
